import { captureException } from "@sentry/react";
import { SENTRY_ENABLED, sentryLogger } from "./sentry";
import { ws } from "./websocket-wrapper";

/**
 * A tab has ONE `ws`, one `core-store` and one `data-cache-store`, and every
 * client signs in with the same account
 * (docs/adr/40-every-elaso-client-shares-one-sign-in.md). What still differs
 * between clients is the data they load onto that socket: `/operations` loads
 * every channel (`channel.listAll`) and its own workspace selection, `/app` loads
 * the channels the user can open and the Learner Client's selection. This module
 * is the arbiter. Exactly one scope has the socket at a time, and moving between
 * clients hands it over with a fresh connection, so each client's loader starts
 * from nothing of the other's.
 *
 * Ownership has a second axis, "which user", because an LTI launch signs a
 * second account in on top of a restored one without changing scope, and
 * `ws.connect()` returns early on a socket that is already open OR still
 * handshaking, so the freshly minted token went into `wsUrl` unused and the tab
 * kept serving the PREVIOUS user's connection, resolving every procedure
 * (`workspace.list` included) against them. A scope handoff is `claimSocket`; an
 * identity change within one scope is `releaseForeignSocket`.
 *
 * The `ws` event listeners and `beforeReconnect` are registered here exactly
 * ONCE, at module load. `beforeReconnect` is a setter, not a listener list.
 */

/**
 * Which client's data the tab's socket is loaded for. `/instance` loads with
 * `app`, because it shares the Learner Client's workspace data.
 */
export type SocketScope = "app" | "operations" | "organization";

export type SessionSocketHooks = {
	/** Whether somebody is signed in. */
	hasUser: () => boolean;
	/** Full token exchange + `ws.connect()`. */
	connect: () => Promise<void>;
	/** Token exchange + `ws.updateUrl` only, the `beforeReconnect` body. */
	refreshUrl: () => Promise<void>;
	setWsConnected: (connected: boolean) => void;
};

let session: SessionSocketHooks | null = null;

/**
 * Which scope the current URL belongs to. Seeding it from the URL (as opposed to
 * defaulting to `"app"` and letting the route correct it) keeps a cold load
 * straight into `/operations` from loading `/app`'s data first.
 * `subscribeToAuthChanges` fires during module init, well before the route's
 * claim effect, so without this the tab would open a connection for the wrong
 * client only to tear it down a moment later.
 */
const detectInitialScope = (): SocketScope => {
	if (typeof window === "undefined") {
		return "app";
	}
	// The desktop build uses hash history; the web build uses paths. Either way
	// the query lives with the route, which on the desktop is inside the hash.
	const { pathname, search, hash } = window.location;
	const route = hash.startsWith("#/") ? hash.slice(1) : `${pathname}${search}`;
	const [path, query] = route.split("?");
	// `?redirect=` FIRST, because the sign-in page at an identity home's address
	// is not on the client it signs into (TECH-3350). `/acme?redirect=/operations`
	// lands on `/operations`, and seeding `app` from the path would open a
	// connection for the wrong client before the route could correct it.
	const redirect = query ? new URLSearchParams(query).get("redirect") : null;
	const target = redirect ?? path;
	if (target.startsWith("/operations")) return "operations";
	if (target.startsWith("/organization")) return "organization";
	return "app";
};

let activeScope: SocketScope = detectInitialScope();

/**
 * Bumped on every claim so an in-flight connect from a superseded claim can bail
 * instead of racing the new owner onto the socket (same guard shape as
 * `core-store`'s `loadGeneration`).
 */
let claimGeneration = 0;

// Which USER the socket carries.
// `urlUid` is whose credential the current socket URL holds; `socketUid` is whose
// credential the socket that actually OPENED holds. They differ in exactly the
// case this split exists for. A token minted for a new user while the previous
// user's socket is still open makes `connect()` no-op and wastes the mint.
// Only an `open` event promotes one to the other, so `socketUid` never claims an
// identity the server did not actually authenticate.
//
// `urlUid` therefore has to describe the socket currently being opened, not just
// the last mint. That holds only because `socketBelongsToAnotherUser` is
// consulted BEFORE a mint that would overwrite it (`auth-store`'s
// `handleSignedIn`). Every other caller of `refreshSocketUrl` re-mints for the
// same user.
let urlUid: string | null = null;
let socketUid: string | null = null;

/**
 * Record whose credential a freshly minted socket URL carries. Called by the
 * auth store right after the token exchange, before `connect()`.
 */
export const noteSocketUrlIdentity = (uid: string | null): void => {
	urlUid = uid;
};

/**
 * Is there a socket in this tab, open, opening, or lately closed, belonging to
 * somebody other than `uid`?
 *
 * Deliberately true for a CLOSED socket that was last opened for another user.
 * Their `core-store` and cache contents are still in this tab, and dropping them
 * is right whether or not the connection happens to be up at this instant.
 *
 * Also true for a socket still HANDSHAKING under another credential, which is a
 * separate reachable state rather than a rounding error. `connect()` returns
 * early on `CONNECTING` exactly as it does on `OPEN` (the two guards at the top
 * of the wrapper's `connect()`), so a mint landing in the handshake window is
 * discarded the same way. Worse, the earlier socket's `open` would then promote
 * the NEWCOMER's `urlUid`, leaving a connection authenticated as one user but
 * recorded as another, with no further release possible. Answering this
 * before the caller mints is what keeps `urlUid` a truthful description of the
 * socket being opened.
 */
export const socketBelongsToAnotherUser = (uid: string): boolean => {
	if (socketUid !== null && socketUid !== uid) {
		return true;
	}
	return ws.isConnecting && urlUid !== null && urlUid !== uid;
};

/**
 * Give up a socket held under another user's credential: close it, drop what was
 * queued for them, and clear the socket-scoped stores. Same teardown
 * `claimSocket` performs for a handoff, for the same reason. The next
 * `connect()` must start from nothing of theirs.
 *
 * Leaves `ui-store` alone, exactly as `claimSocket` does. On a launch that is the
 * caller's job (`lib/lti/launch-flow.ts`), which knows an account switch from a
 * teacher relaunching the same course.
 */
export const releaseForeignSocket = async (): Promise<void> => {
	ws.disconnect();
	ws.clearMessageQueue();
	ws.clearPendingCalls();
	// Both axes. The socket is closed (or its handshake aborted), so neither the
	// opened identity nor the URL's pending one describes anything any more. Leaving
	// `urlUid` set would report the same socket foreign a second time and tear the
	// incoming user's own state down under them.
	socketUid = null;
	urlUid = null;
	await resetSocketScopedStores();
};

export const getActiveScope = (): SocketScope => activeScope;

export const registerSession = (hooks: SessionSocketHooks): void => {
	session = hooks;
};

ws.addEventListener("open", () => {
	// The socket that opened carries the token the URL held. Record WHOSE, so a
	// later sign-in can tell "already connected" from "connected as somebody else".
	socketUid = urlUid;
	console.log("[WebSocket Client] Connected");
	sentryLogger.info("WebSocket connected", { scope: activeScope });
	session?.setWsConnected(true);
});

ws.addEventListener("close", (event: Event) => {
	const closeEvent = event as CloseEvent;
	console.log(
		`[WebSocket Client] Disconnected (code: ${closeEvent.code}, reason: ${closeEvent.reason})`
	);
	sentryLogger.warn("WebSocket disconnected", {
		code: closeEvent.code,
		reason: closeEvent.reason,
		scope: activeScope,
	});
	session?.setWsConnected(false);
});

// Throwing here makes `scheduleReconnect` retry rather than connect with a stale
// or foreign token.
ws.beforeReconnect = async () => {
	if (!session) {
		throw new Error("No sign-in registered for the socket");
	}
	if (!session.hasUser()) {
		throw new Error("Nobody is signed in");
	}
	console.log("[WebSocket Client] Refreshing auth token before reconnect...");
	await session.refreshUrl();
};

/**
 * Data belonging to whichever client last held the socket. Deliberately does
 * NOT touch `ui-store` or `ops-ui-store`, which hold each client's own drafts,
 * layout and workspace selection, and survive moving between clients.
 */
const resetSocketScopedStores = async (): Promise<void> => {
	const [{ coreStore }, { dataCacheStore }] = await Promise.all([
		import("./stores/core-store"),
		import("./stores/data-cache-store"),
	]);
	coreStore.getState().reset();
	dataCacheStore.getState().reset();
};

/**
 * Hand the socket to `scope`. No-op when it already has it, so a remount or a
 * re-entry costs nothing. Otherwise: tear the socket down, drop anything queued
 * for the old scope, wipe the socket-scoped stores, then reconnect if somebody
 * is signed in (if nobody is, the route sends them to sign in and the connect
 * happens from the auth-state handler once they have).
 */
export const claimSocket = async (scope: SocketScope): Promise<void> => {
	if (activeScope === scope) {
		return;
	}

	claimGeneration += 1;
	const generation = claimGeneration;
	// Flip ownership synchronously, before any await. A `beforeReconnect` or
	// auth-state change landing in the await window below must already see the new
	// scope, or the outgoing one would quietly reconnect itself.
	activeScope = scope;

	ws.disconnect();
	ws.clearMessageQueue();
	ws.clearPendingCalls();
	session?.setWsConnected(false);

	await resetSocketScopedStores();
	if (claimGeneration !== generation) {
		return;
	}

	if (!session?.hasUser()) {
		return;
	}

	try {
		await session.connect();
	} catch (error) {
		if (claimGeneration !== generation) {
			return;
		}
		console.error("[WebSocket Client] Failed to connect after claim:", error);
		sentryLogger.error("WebSocket connection failed after socket claim", {
			scope,
		});
		if (SENTRY_ENABLED) {
			captureException(error, { tags: { context: "socket_claim_connect" } });
		}
	}
};

/**
 * Connects again now, for a screen that has waited too long: a fresh token,
 * then `ws.connect()`, which cancels any reconnect the wrapper has scheduled.
 * After a failed first token exchange this is the only way back, because
 * nothing else retries one. Does nothing when nobody is signed in, or when the
 * socket is already open.
 *
 * The token comes first, so a failed exchange leaves the socket as it was,
 * the wrapper's own reconnect included. Only then is a handshake still waiting
 * for an answer dropped, because one the server never answers stays
 * connecting until the browser gives up, and `ws.connect()` would wait on it.
 */
export const retryConnection = async (): Promise<void> => {
	if (!session?.hasUser() || ws.isConnected) {
		return;
	}
	try {
		await session.refreshUrl();
		if (ws.isConnecting) {
			ws.disconnect();
		}
		ws.connect();
	} catch (error) {
		console.error("[WebSocket Client] Failed to connect on retry:", error);
		sentryLogger.error("WebSocket connection failed on retry", {
			scope: activeScope,
		});
		if (SENTRY_ENABLED) {
			captureException(error, { tags: { context: "socket_retry_connect" } });
		}
	}
};

/**
 * Test seam: force which scope has the socket. Deliberately leaves the session
 * registration intact. The auth store registers exactly once, at module init, so
 * clearing it would un-wire the real store for the rest of the file.
 */
export const __test__setActiveScope = (scope: SocketScope): void => {
	activeScope = scope;
	claimGeneration = 0;
	urlUid = null;
	socketUid = null;
};

/** Test seam: pretend a socket opened under `uid`'s credential. */
export const __test__setSocketIdentity = (uid: string | null): void => {
	urlUid = uid;
	socketUid = uid;
};
